interface AskClaudeParams {
  system: string;
  prompt: string;
  maxTokens?: number;
}

type AskClaudeFn = (params: AskClaudeParams) => Promise<unknown>;

// Thin proxy — prompt-building and response interpretation happen client-side; the backend only calls Claude and extracts JSON out of the raw text.
//
// `fetch-utils` is imported lazily rather than at module scope. It transitively
// pulls in the app's auth stack, which reads `import.meta.env` and therefore
// only evaluates under Vite. Deferring it lets the offline harness import this
// module (and everything downstream of it, i.e. the real optimizer) under plain
// Node. In the browser this resolves to the same already-shared chunk on first
// call, so there is no practical bundling or latency cost.
const askClaudeViaLambda: AskClaudeFn = async ({ system, prompt, maxTokens }) => {
  const { awaitJSON, CommerceOpsLambdaFetch } = await import('../../../utils/fetch-utils');
  const res = await CommerceOpsLambdaFetch('/scraper/amazon/scorecard/ask', {
    method: 'POST',
    body: { system, prompt, maxTokens }
  });
  return awaitJSON<unknown>(res);
};

/**
 * Offline-harness seam. The app NEVER sets this — in the browser it stays null
 * and every call goes through the Lambda proxy exactly as before. The offline
 * prompt-tuning harness (`harness/`, run under tsx in a dev pod) swaps in a
 * direct Bedrock transport so it can exercise the REAL optimizeTitleForCap /
 * optimizeItemHighlight code paths — retries, cap enforcement, the too-short
 * floor — instead of re-implementing them and drifting out of sync.
 *
 * Why a seam rather than exporting the prompt builders: the defects we tune
 * against live as much in the retry/guarantee control flow as in the prompt
 * text, so the harness has to run the real functions end to end.
 */
let transportOverride: AskClaudeFn | null = null;

/** Harness-only. Pass null to restore the default Lambda transport. */
export const setAskClaudeTransport = (fn: AskClaudeFn | null): void => {
  transportOverride = fn;
};

export const askClaude: AskClaudeFn = params => (transportOverride ?? askClaudeViaLambda)(params);
